import { useTranslator } from '@Localization';
import { useInjection } from 'inversify-react';
import { checkAll, checkWorkspaceRole, IPermissionCheckResult } from '../../../modules/permissions/permissionsHelpers';
import { ICurrentWorkspaceStore } from '../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { Plan } from '../user/user.store';
import { WorkspaceRole } from '../workspaces/workspaces.store';

interface IProjectsPermissions {
  canView(): IPermissionCheckResult;
  canDelete(): IPermissionCheckResult;
  canEdit(): IPermissionCheckResult;
  canExport(): IPermissionCheckResult;
  canImport(): IPermissionCheckResult;
  canPublish(): IPermissionCheckResult;
  canSeePublish(): IPermissionCheckResult;
  canCreate(): IPermissionCheckResult;
  canViewCreatedBy(): IPermissionCheckResult;
}

export const useProjectPermissions = (): IProjectsPermissions => {
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);
  const currentWorkspace = currentWorkspaceStore.currentWorkspace;
  const { t } = useTranslator();

  const canDelete = (): IPermissionCheckResult => {
    return checkWorkspaceRole([WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Developer, WorkspaceRole.Trainer], currentWorkspace, t);
  };

  const canEdit = (): IPermissionCheckResult => {
    return checkWorkspaceRole([WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Developer, WorkspaceRole.Trainer], currentWorkspace, t);
  };

  const canExport = (): IPermissionCheckResult => {
    return checkWorkspaceRole([WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Developer, WorkspaceRole.Trainer], currentWorkspace, t);
  };

  const canImport = (): IPermissionCheckResult => {
    return checkAll(
      [Plan.Admin, Plan.Training, Plan.Annotation],
      [WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Developer, WorkspaceRole.Trainer],
      currentWorkspace,
      t
    );
  };

  const canPublish = (): IPermissionCheckResult => {
    return checkAll(
      [Plan.Admin, Plan.Training, Plan.Annotation, Plan.Free],
      [WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Developer, WorkspaceRole.Trainer],
      currentWorkspace,
      t
    );
  };

  const canSeePublish = (): IPermissionCheckResult => {
    return checkAll(
      [Plan.Admin, Plan.Training, Plan.Annotation, Plan.Free, Plan.Inactive],
      [WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Developer, WorkspaceRole.Trainer],
      currentWorkspace,
      t
    );
  };

  const canView = (): IPermissionCheckResult => {
    return checkWorkspaceRole(
      [
        WorkspaceRole.Owner,
        WorkspaceRole.Manager,
        WorkspaceRole.Developer,
        WorkspaceRole.Trainer,
        WorkspaceRole.Collaborator,
        WorkspaceRole.Guest,
        WorkspaceRole.Assistant,
      ],
      currentWorkspace,
      t
    );
  };

  const canCreate = (): IPermissionCheckResult => {
    return checkAll(
      [Plan.Admin, Plan.Training, Plan.Annotation, Plan.Free],
      [WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Developer, WorkspaceRole.Trainer],
      currentWorkspace,
      t
    );
  };

  const canViewCreatedBy = (): IPermissionCheckResult => {
    return checkAll(
      [Plan.Admin, Plan.Training, Plan.Annotation, Plan.Free, Plan.Inactive],
      [WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Developer, WorkspaceRole.Trainer],
      currentWorkspace,
      t
    );
  };

  return {
    canView,
    canDelete,
    canEdit,
    canExport,
    canImport,
    canPublish,
    canSeePublish,
    canCreate,
    canViewCreatedBy,
  };
};
